import React, { useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select,
} from '@mui/material';

interface DeliveryMethodChoice {
    /** Numeric delivery method id, or empty string for the placeholder entry. */
    id: number | string;
    /** Human-readable label. */
    name: string;
}

interface BulkDeliveryMethodDialogProps {
    /** Whether the dialog is visible. */
    open: boolean;
    /** Number of currently selected rows, shown in the dialog title. */
    selectedCount: number;
    /** Whether a save request is in flight. Disables the Save button while true. */
    loading: boolean;
    /** Available delivery-method choices rendered in the dropdown. */
    deliveryMethod: DeliveryMethodChoice[];
    /** Called when the user dismisses the dialog without saving. */
    onClose: () => void;
    /**
     * Called when the user clicks Save.
     * @param deliveryMethodId - The numeric id of the chosen delivery method.
     */
    onSave: (deliveryMethodId: number) => void;
}

/**
 * Modal dialog that lets the user pick a new delivery method to apply in bulk
 * to all selected Event Delivery rows.
 *
 * Internal state (the currently-selected method) resets to `0` each time the
 * dialog opens, keeping parent components lean.
 *
 * @example
 * <BulkDeliveryMethodDialog
 *   open={dialogOpen}
 *   selectedCount={selectedRows.length}
 *   loading={isSaving}
 *   deliveryMethod={deliveryMethodChoices}
 *   onClose={() => setDialogOpen(false)}
 *   onSave={(id) => handleBulkSave(id)}
 * />
 */
const BulkDeliveryMethodDialog: React.FC<BulkDeliveryMethodDialogProps> = ({
    open,
    selectedCount,
    loading,
    deliveryMethod,
    onClose,
    onSave,
}) => {
    const [selectedMethod, setSelectedMethod] = useState<number>(0);

    const handleSave = () => {
        onSave(selectedMethod);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Update Delivery Method for {selectedCount} Selected Row(s)</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id="bulk-delivery-method-label">Delivery Method</InputLabel>
                    <Select
                        labelId="bulk-delivery-method-label"
                        value={selectedMethod}
                        label="Delivery Method"
                        onChange={(e) => setSelectedMethod(e.target.value as number)}
                    >
                        {deliveryMethod.map(item => item.id !== '' && (
                            <MenuItem key={item.id} value={item.id as number}>{item.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={loading}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkDeliveryMethodDialog;
